import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Dialog } from '@headlessui/react';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useToast } from '../../../context/ToastContext';
import { useInternshipReviewPrep } from '../../../hooks/useInternshipReviewPrep';
import { useReviewPreparation } from '../../../hooks/useReviewPreparation';
import { CareerFocusWithMetrics } from '../../../hooks/useCareerFocus';
import TimelineHeader from './TimelineHeader';
import EvidenceHealthCheck from './EvidenceHealthCheck';
import SmartRecommendations from './SmartRecommendations';
import PreparationWizard from './PreparationWizard';
import SavedPreparationViewer from './SavedPreparationViewer';
import FinalWeekChecklist from './FinalWeekChecklist';
import { CalendarDaysIcon, CheckCircleIcon, DocumentDuplicateIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';

interface ReviewPrepTabProps {
  focus: CareerFocusWithMetrics;
  onQuickAdd?: (rubricItemId?: string, category?: string, description?: string) => void;
}

const ReviewPrepTab: React.FC<ReviewPrepTabProps> = ({ focus, onQuickAdd }) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  
  // Memoize focus ID to prevent unnecessary re-renders
  const focusId = React.useMemo(() => focus.id, [focus.id]);
  const { metrics, loading, isReviewPeriod } = useInternshipReviewPrep(focusId);
  const { getReviewPreparation, saveReviewPreparation, deleteReviewPreparation } = useReviewPreparation();
  const [showWizard, setShowWizard] = useState(false);
  const [completedPrep, setCompletedPrep] = useState(false);
  const [savedPrepData, setSavedPrepData] = useState<any>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Check for existing preparation on mount
  useEffect(() => {
    const checkExistingPrep = async () => {
      const existing = await getReviewPreparation(focus.id);
      if (existing) {
        setCompletedPrep(true);
        setSavedPrepData(existing);
      }
    };
    checkExistingPrep();
  }, [focus.id, getReviewPreparation]);
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Not yet in review period
  if (!isReviewPeriod) {
    const daysUntilActivation = Math.ceil((metrics.activationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${bgColor} rounded-lg border ${borderColor} p-8 text-center`}
      >
        <CalendarDaysIcon className={`w-16 h-16 mx-auto mb-4 ${mutedTextColor}`} />
        <h3 className={`text-lg font-semibold mb-2 ${textColor}`}>
          Review Prep Coming Soon
        </h3>
        <p className={mutedTextColor}>
          This feature will be available in {daysUntilActivation} days as you approach your internship completion.
        </p>
        <p className={`text-sm mt-2 ${mutedTextColor}`}>
          Activation date: {metrics.activationDate.toLocaleDateString()}
        </p>
      </motion.div>
    );
  }
  
  const handleQuickAdd = (rubricItemId: string, category?: string, description?: string) => {
    // Open the quick add modal with pre-selected rubric item
    if (onQuickAdd) {
      onQuickAdd(rubricItemId, category, description);
    }
  };
  
  const handleRecommendationAction = (recommendation: any) => {
    if (recommendation.actionType === 'add_evidence' && recommendation.relatedRubricItem) {
      handleQuickAdd(recommendation.relatedRubricItem);
    } else {
      // Handle other action types
      console.log('Handle recommendation:', recommendation);
    }
  };
  
  
  const handleWizardComplete = async (data: { reflections: any; talkingPoints: any; aiTalkingPoints?: any }) => {
    setShowWizard(false);
    
    const metricsSnapshot = {
      overallCoverage: metrics.overallCoverageScore,
      totalCards: metrics.totalCards,
      criticalGaps: metrics.criticalGaps,
      rubricCoverage: metrics.rubricCoverage
    };
    
    try {
      // Save to database
      const saved = await saveReviewPreparation(
        focus.id,
        data.reflections,
        data.talkingPoints,
        metricsSnapshot,
        data.aiTalkingPoints
      );
      
      if (saved) {
        // Mark as completed
        setCompletedPrep(true);
        setSavedPrepData(saved);
        
        // Show success toast
        showToast(
          '🎉 Review preparation complete! Your talking points have been saved.',
          'success'
        );
        
        // Copy a summary to clipboard
        const summary = `Review Preparation Summary\n\nCoverage: ${metrics.overallCoverageScore}%\nEvidence Cards: ${metrics.totalCards}\n\nYour talking points and reflections have been saved.`;
        navigator.clipboard.writeText(summary);
        
        // Migrate from localStorage if it exists
        const oldData = localStorage.getItem(`review-prep-${focus.id}`);
        if (oldData) {
          localStorage.removeItem(`review-prep-${focus.id}`);
        }
      }
    } catch (error) {
      console.error('Error saving preparation data:', error);
      showToast('Failed to save preparation data. Please try again.', 'error');
    }
  };
  
  const handleDeletePrep = async () => {
    const success = await deleteReviewPreparation(focus.id);
    if (success) {
      setCompletedPrep(false);
      setSavedPrepData(null);
      setShowDeleteConfirm(false);
    }
  };
  
  const handleCopyPreparation = () => {
    if (!savedPrepData) return;
    
    const cleanText = (text: string): string => {
      if (!text) return '';
      return text
        .replace(/\\'/g, "'")
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\');
    };
    
    const date = new Date(savedPrepData.completed_at).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    let formattedText = `PERFORMANCE REVIEW PREPARATION SUMMARY\n`;
    formattedText += `════════════════════════════════════════\n\n`;
    formattedText += `📅 Prepared on: ${date}\n\n`;
    
    // Add metrics summary if available
    if (savedPrepData.metrics_snapshot) {
      formattedText += `📊 COVERAGE METRICS\n`;
      formattedText += `───────────────────\n`;
      formattedText += `• Overall Coverage: ${savedPrepData.metrics_snapshot.overallCoverage || 0}%\n`;
      formattedText += `• Evidence Cards: ${savedPrepData.metrics_snapshot.totalCards || 0}\n`;
      if (savedPrepData.metrics_snapshot.criticalGaps?.length > 0) {
        formattedText += `• Critical Gaps: ${savedPrepData.metrics_snapshot.criticalGaps.length}\n`;
      }
      formattedText += `\n`;
    }
    
    // Add reflections
    if (savedPrepData.reflections) {
      formattedText += `💭 REFLECTIONS\n`;
      formattedText += `───────────────\n`;
      
      if (savedPrepData.reflections.biggestWin) {
        formattedText += `\n🎯 Biggest Win:\n`;
        formattedText += `${cleanText(savedPrepData.reflections.biggestWin)}\n`;
      }
      
      if (savedPrepData.reflections.keyLearnings) {
        formattedText += `\n📚 Key Learnings:\n`;
        formattedText += `${cleanText(savedPrepData.reflections.keyLearnings)}\n`;
      }
      
      if (savedPrepData.reflections.challenges) {
        formattedText += `\n💪 Challenges Overcome:\n`;
        formattedText += `${cleanText(savedPrepData.reflections.challenges)}\n`;
      }
      
      if (savedPrepData.reflections.growth) {
        formattedText += `\n🌱 Growth & Development:\n`;
        formattedText += `${cleanText(savedPrepData.reflections.growth)}\n`;
      }
      
      if (savedPrepData.reflections.proudestMoment) {
        formattedText += `\n⭐ Proudest Moment:\n`;
        formattedText += `${cleanText(savedPrepData.reflections.proudestMoment)}\n`;
      }
      
      if (savedPrepData.reflections.futureGoals) {
        formattedText += `\n🎯 Future Goals:\n`;
        formattedText += `${cleanText(savedPrepData.reflections.futureGoals)}\n`;
      }
      
      formattedText += `\n`;
    }
    
    // Add talking points
    if (savedPrepData.talking_points) {
      formattedText += `🎯 KEY TALKING POINTS\n`;
      formattedText += `─────────────────────\n`;
      
      if (savedPrepData.talking_points.openingStatement) {
        formattedText += `\n🎤 Opening Statement:\n`;
        formattedText += `${cleanText(savedPrepData.talking_points.openingStatement)}\n`;
      }
      
      if (savedPrepData.talking_points.strengthHighlights?.length > 0) {
        formattedText += `\n💪 Strength Highlights:\n`;
        savedPrepData.talking_points.strengthHighlights.forEach((highlight: string) => {
          formattedText += `• ${cleanText(highlight)}\n`;
        });
      }
      
      if (savedPrepData.talking_points.growthExamples?.length > 0) {
        formattedText += `\n📈 Growth Examples:\n`;
        savedPrepData.talking_points.growthExamples.forEach((example: string) => {
          formattedText += `• ${cleanText(example)}\n`;
        });
      }
      
      if (savedPrepData.talking_points.challengeNarratives?.length > 0) {
        formattedText += `\n🎯 Challenge Stories:\n`;
        savedPrepData.talking_points.challengeNarratives.forEach((narrative: string) => {
          formattedText += `• ${cleanText(narrative)}\n`;
        });
      }
      
      if (savedPrepData.talking_points.futureCommitments?.length > 0) {
        formattedText += `\n🚀 Future Commitments:\n`;
        savedPrepData.talking_points.futureCommitments.forEach((commitment: string) => {
          formattedText += `• ${cleanText(commitment)}\n`;
        });
      }
      
      if (savedPrepData.talking_points.questionsForManager?.length > 0) {
        formattedText += `\n❓ Questions for Manager:\n`;
        savedPrepData.talking_points.questionsForManager.forEach((question: string) => {
          formattedText += `• ${cleanText(question)}\n`;
        });
      }
      
      if (savedPrepData.talking_points.closingThoughts) {
        formattedText += `\n🎬 Closing Thoughts:\n`;
        formattedText += `${cleanText(savedPrepData.talking_points.closingThoughts)}\n`;
      }
    }
    
    navigator.clipboard.writeText(formattedText);
    showToast('Review preparation summary copied to clipboard!', 'success');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Simplified Success Banner */}
      {completedPrep && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${darkMode ? 'bg-green-900/20' : 'bg-green-50'} border ${
            darkMode ? 'border-green-800' : 'border-green-200'
          } rounded-lg p-4`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <div>
                <h4 className={`font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                  Review preparation complete
                </h4>
                <p className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                  Last updated {new Date(savedPrepData?.completed_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowViewer(true)}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                View
              </button>
              <span className={`text-sm ${mutedTextColor}`}>•</span>
              <button
                onClick={handleCopyPreparation}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Copy
              </button>
              <span className={`text-sm ${mutedTextColor}`}>•</span>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Delete
              </button>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Status and Actions */}
        <div className="lg:col-span-1 space-y-6">
          {/* Timeline Status Card */}
          <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
            <TimelineHeader 
              daysUntilEnd={metrics.daysUntilEnd}
              isUrgent={metrics.daysUntilEnd <= 7}
            />
            
            {/* Primary Action Button */}
            <div className="mt-6">
              <button
                onClick={() => setShowWizard(true)}
                className={`w-full px-6 py-3 ${
                  completedPrep 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2`}
              >
                {completedPrep ? 'Update Preparation' : 'Start Review Preparation'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Final Week Checklist - Only shows when <= 7 days left */}
          {metrics.daysUntilEnd <= 7 && (
            <FinalWeekChecklist
              focusId={focus.id}
              daysUntilReview={metrics.daysUntilReview}
              hasCompletedPrep={completedPrep}
              evidenceCoverage={metrics.overallCoverage}
            />
          )}
          
          {/* Smart Recommendations */}
          {metrics.recommendations.length > 0 && (
            <SmartRecommendations 
              recommendations={metrics.recommendations}
              onActionClick={handleRecommendationAction}
              focusId={focus.id}
            />
          )}
        </div>
        
        {/* Right Column - Evidence Health Check */}
        <div className="lg:col-span-2">
          <EvidenceHealthCheck 
            coverage={metrics.rubricCoverage}
            onQuickAdd={handleQuickAdd}
          />
        </div>
      </div>
      
      {/* Preparation Wizard Modal */}
      {showWizard && (
        <PreparationWizard
          focus={focus}
          metrics={metrics}
          onClose={() => setShowWizard(false)}
          onComplete={handleWizardComplete}
          existingData={savedPrepData ? {
            reflections: savedPrepData.reflections,
            talkingPoints: savedPrepData.talking_points,
            aiTalkingPoints: savedPrepData.ai_talking_points
          } : undefined}
        />
      )}
      
      {/* Saved Preparation Viewer */}
      {savedPrepData && (
        <SavedPreparationViewer
          isOpen={showViewer}
          onClose={() => setShowViewer(false)}
          preparationData={savedPrepData}
        />
      )}
      
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className={`mx-auto max-w-sm w-full ${bgColor} rounded-xl shadow-xl p-6`}>
              <Dialog.Title className={`text-lg font-semibold mb-4 ${textColor}`}>
                Delete Review Preparation?
              </Dialog.Title>
              
              <p className={`mb-6 ${mutedTextColor}`}>
                Are you sure you want to delete your review preparation? This will remove all your reflections and talking points. This action cannot be undone.
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePrep}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                >
                  Delete
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </motion.div>
  );
};

export default ReviewPrepTab;